import { useModal } from '@ebay/nice-modal-react'
import { useNavigate, useParams } from '@tanstack/router'
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { LazyLoadImage } from 'react-lazy-load-image-component'

import hot_badge from '../../assets/casino/hot_badge.webp'
import play_game from '../../assets/common/play_game.webp'
import useDimensions from '../../hooks/useDimensions'
import useGeoCheck from '../../hooks/useGeoCheck'
import useTranslateImage from '../../hooks/useTranslateImage'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { Game } from '../../types/game'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'
import { random } from '../../utils/collection'
import { numberWithCommas } from '../../utils/number'
import { Spinner, ToastMessage } from '../common'
import LoginModal from '../common/log-in-modal'
import Icon from '../icon'

interface GamesContainerProps {
  games: Game[]
  favorites: Game[]
  setFavorites: Dispatch<SetStateAction<Game[]>>
  categoryKey: string
  hideJackpot?: boolean
  containerStyle?: string
  loadMoreLoading: boolean
  display?: 'row' | 'column'
}

export const GameImage = ({
  item,
  favorites,
  setFavorites,
  hideJackpot,
  containerStyle = '',
}: Omit<GamesContainerProps, 'games' | 'loadMoreLoading'> & { item: Game }) => {
  const navigate = useNavigate()
  const { category } = useParams({ from: '/casino/$category' })
  const pending_bind = useBoundStore(state => state.user.pending_bind)
  const setIsPendingDepositVisible = useBoundStore(
    state => state.common.setIsPendingDepositVisible,
  )
  const username = useBoundStore(state => state.user.username)
  const { geoCheck } = useGeoCheck()
  const toastMessage = useModal(ToastMessage)
  const logInModal = useModal(LoginModal)
  const { isDesktop } = useDimensions()
  const translateImage = useTranslateImage()
  const [isHovered, setIsHovered] = useState(false)
  const [jackpotAmount, setJackpotAmount] = useState<null | number>(
    item.gameInfo.jackpot_amount
      ? (item.gameInfo.jackpot_amount as number)
      : item.categoryId.includes('15656')
      ? random(500000, 1500000)
      : null,
  )
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const isFavorite = useMemo(() => {
    return favorites.find(
      fav => fav.gameInfo.gameCode === item.gameInfo.gameCode,
    )
  }, [favorites])

  const removeFavorite = (selected: Game) => {
    const reqBody = {
      type: 'remove_game_collect',
      ...selected.gameInfo,
    }
    api.post('/ajax_data.php', reqBody)
    setFavorites(prev =>
      prev.filter(
        game => game.gameInfo.gameCode !== selected.gameInfo.gameCode,
      ),
    )
  }

  const addFavorite = (game: Game) => {
    const reqBody = {
      type: 'add_game_collect',
      ...game.gameInfo,
    }
    api.post('/ajax_data.php', reqBody)
    setFavorites(prev => [...prev, game])
  }

  const onPlayGame = async (game: Game) => {
    // Transfer balance to game
    api.post('/ajax_data.php', {
      type: 'auto_transfer_in',
      ...game.gameInfo,
    })

    setIsLoading(true)
    const reqBody = {
      type: 'play_game_websocket2',
      id: game.gameInfo.id,
      platform: isDesktop ? 0 : 1,
    }

    const response = await api.post<ApiResponse<string>>(
      '/request.php',
      reqBody,
    )

    const { info, status } = response.data
    if (status === 1) {
      navigate({
        to: '/casino/$category/$game',
        search: {
          gameUrl: encodeURIComponent(info),
          gameId: game.gameInfo.gameId,
          gameCode: game.gameInfo.gameCode,
          isFavorite: isFavorite ? '1' : '0',
        },
        params: {
          category,
          game: `${game.name.replaceAll(/\W/g, '')}-${game.gameInfo.id}`,
        },
      })
    } else if (typeof info === 'string') {
      toastMessage.show({
        text: info,
        isValid: false,
      })
    }
    setIsLoading(false)
  }

  const badge = useMemo(() => {
    if (item.categoryId.includes('15652')) {
      return (
        <div
          className={classNames(
            jackpotAmount && 'top-[18px] xl:top-[27px]',
            'absolute z-[2] left-0.5 top-0.5',
          )}>
          <img
            className="w-10 xl:w-[65px]"
            src={translateImage('new-badge')}
            alt="New"
          />
        </div>
      )
    } else if (item.categoryId.includes('15663')) {
      return (
        <div
          className={classNames(
            jackpotAmount && 'top-[18px] xl:top-[27px]',
            'absolute z-[2] left-0.5 top-0.5',
          )}>
          <img className="w-10 xl:w-[65px]" src={hot_badge} alt="Hot" />
        </div>
      )
    }

    return null
  }, [item])

  useEffect(() => {
    if (!jackpotAmount) return
    const interval = setInterval(() => {
      setJackpotAmount(prev => (prev ? prev + Math.random() * 100 : prev))
    }, 1000)

    return () => clearInterval(interval)
  }, [jackpotAmount])

  return (
    <div
      className={classNames(
        isImageLoaded && 'after:hidden',
        `aspect-square relative w-full flex justify-center items-center overflow-hidden cursor-pointer',
        'bg-secondary rounded-[10px] after:absolute after:-translate-x-full after:animate-shimmer after:content-['']',
        'after:inset-0 after:bg-[linear-gradient(_90deg,transparent_0,var(--primary-10)_20%,var(--primary-50)_60%,transparent_)]`,
        containerStyle,
      )}
      onClick={() => {
        if (!username) {
          return geoCheck(logInModal.show)
        }
        if (+pending_bind === 0 || +pending_bind === 2) {
          setIsPendingDepositVisible(true)
          return
        }
        onPlayGame(item)
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      {isHovered && isDesktop && (
        <div className="absolute z-[3] w-full h-full bg-[rgba(0,0,0,0.6)] left-0 top-0 flx-ctr">
          <img
            className="w-[120px] h-[120px]"
            src={play_game}
            alt="Play Game"
          />
        </div>
      )}
      {isLoading && (
        <div className="absolute z-[3] w-full h-full bg-[rgba(0,0,0,0.6)] left-0 top-0 flx-ctr">
          <Spinner />
        </div>
      )}
      <LazyLoadImage
        src={item.imgURL}
        alt={item.name}
        width={'100%'}
        height={'100%'}
        onLoad={() => setIsImageLoaded(true)}
        className={classNames(
          isHovered && 'scale-110',
          'z-[1] w-auto h-full aspect-square object-cover transition-all duration-300 ease-in-out',
          isImageLoaded ? 'opacity-1' : 'opacity-0',
        )}
      />
      {!!jackpotAmount && !hideJackpot && (
        <p className="jackpot w-full absolute text-[11px] font-HelveticaNeue-CondensedBold text-white text-center z-[2] pt-0.5 pb-1 px-0 left-0 top-0 xl:text-xl">
          ${numberWithCommas(jackpotAmount)}
        </p>
      )}
      <div
        className={classNames(
          'w-9 h-9 absolute top-[-9px] right-[-7.5px] z-[3] cursor-pointer p-3',
          'xl:w-[50px] xl:h-[50px] xl:top-[-11px] xl:right-[-11px] flx-ctr [&>svg]:w-3 [&>svg]:h-3 [&>svg]:xl:w-5 [&>svg]:xl:h-5',
        )}
        onClick={e => {
          e.stopPropagation()
          if (!username) {
            geoCheck(logInModal.show)
          } else if (isFavorite) removeFavorite(item)
          else addFavorite(item)
        }}>
        {isFavorite ? (
          <Icon id="star" fill="#fff" />
        ) : (
          <Icon id="star-outline" fill="#fff" />
        )}
      </div>
      <div className="w-8 h-8 absolute z-[2] border-l-transparent border-b-transparent border-[16px] border-solid border-[rgba(0,0,0,0.4)] right-0 top-0 xl:w-[50px] xl:h-[50px] xl:border-[25px]" />
      {badge}
    </div>
  )
}

const GamesContainer = ({
  games,
  categoryKey,
  loadMoreLoading,
  display = 'column',
  ...favoritesProps
}: GamesContainerProps) => {
  return (
    <div
      className={classNames(
        'gap-2 relative py-0 xl:gap-[15px] 4xl:py-0',
        display === 'column'
          ? 'grid grid-cols-[repeat(3,1fr)] xl:grid-cols-[repeat(5,1fr)] xl:px-[90px] xl:py-0 px-2 3xl:px-[150px] 3xl:py-0 4xl:px-[250px]'
          : 'flex justify-start items-center overflow-auto no-scrollbar',
      )}>
      {!['favorites'].includes(categoryKey) && loadMoreLoading
        ? Array.from(Array(45).keys()).map(key => (
            <div
              key={key}
              className={classNames(
                display === 'row'
                  ? 'min-w-[calc(100%/4.3)] xl:min-w-[calc(100%/8)]'
                  : 'w-full',
                'aspect-square relative overflow-hidden cursor-pointer flex justify-center items-center bg-secondary rounded-[10px]',
                'after:absolute after:-translate-x-full after:bg-[linear-gradient(_90deg,transparent_0,var(--primary-10)_20%,var(--primary-50)_60%,transparent_)]',
                "after:animate-shimmer after:content-[''] after:inset-0",
              )}
            />
          ))
        : games?.map((item, index) => (
            <GameImage
              key={item.id + index}
              categoryKey={categoryKey}
              item={item}
              {...favoritesProps}
              containerStyle={
                display === 'row'
                  ? 'min-w-[calc(100%/4.3)] xl:min-w-[calc(100%/8)]'
                  : ''
              }
            />
          ))}
    </div>
  )
}

export default GamesContainer
